// Twitch clips and VODs open in a fullscreen overlay instead of linking out. The
// whole scene (pattern backdrop + the centred embed) is clipped into the brand
// play-button shape (a CSS mask) that grows from a point to past the viewport on open
// and shrinks back on close — mirroring the stream intro's shape transition. The
// thumbnail links keep their href, so without JS (or on a modified click) they still
// navigate to Twitch.

type MediaType = 'clip' | 'video';

// Reads a CSS time custom property (e.g. "520ms" or "0.52s") off an element so the
// staged close stays in sync with the SCSS transition durations.
const readMs = (element: HTMLElement, name: string, fallback: number): number => {
    const raw = getComputedStyle(element).getPropertyValue(name).trim();

    if (raw === '') {
        return fallback;
    }

    const value = Number.parseFloat(raw);

    if (Number.isNaN(value)) {
        return fallback;
    }

    return raw.endsWith('ms') ? value : value * 1000;
};

const embedSrc = (id: string, type: MediaType): string | null => {
    const parent = window.location.hostname;

    if (type === 'clip') {
        return `https://clips.twitch.tv/embed?clip=${encodeURIComponent(id)}&parent=${encodeURIComponent(parent)}&autoplay=true&muted=false`;
    }

    if (type === 'video') {
        return `https://player.twitch.tv/?video=${encodeURIComponent(id)}&parent=${encodeURIComponent(parent)}&autoplay=true&muted=false`;
    }

    return null;
};

export function initVideoPlayer(): void {
    const shell = document.querySelector<HTMLElement>('[data-swl-player]');

    if (shell === null) {
        return;
    }

    const scene = shell.querySelector<HTMLElement>('[data-swl-player-scene]');
    const stage = shell.querySelector<HTMLElement>('[data-swl-player-stage]');
    const closeButton = shell.querySelector<HTMLButtonElement>('[data-swl-player-close]');
    const embed = shell.querySelector<HTMLElement>('[data-swl-player-embed]');

    if (scene === null || stage === null || embed === null) {
        return;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealMs = readMs(shell, '--swl-player-reveal', 520);
    const fadeMs = readMs(shell, '--swl-player-fade', 320);

    let trigger: HTMLElement | null = null;
    let closeTimer = 0;
    let closing = false;

    // The overlay covers the page visually and traps focus, but the page behind it
    // stays in the DOM. Take every sibling out of the tab order and a11y tree while open.
    const inertSiblings = (on: boolean): void => {
        for (const node of Array.from(document.body.children)) {
            if (node instanceof HTMLElement && node !== shell) {
                node.toggleAttribute('inert', on);
            }
        }
    };

    const mountEmbed = (src: string, title: string): void => {
        const iframe = document.createElement('iframe');

        // Set the autoplay permission and every other attribute before the src, so the
        // permission policy is in place the moment the frame starts loading. Combined
        // with the click that opened the overlay (a fresh user activation), this gives
        // the embed the best shot at starting unmuted.
        iframe.allow = 'autoplay; fullscreen';
        iframe.allowFullscreen = true;
        iframe.setAttribute('allowfullscreen', '');
        iframe.title = title;
        iframe.src = src;

        embed.replaceChildren(iframe);
    };

    // Hand the page back the moment the shape has shrunk away: drop the scroll lock and
    // inert, restore focus, and make the overlay itself inert so the still-visible dim
    // fades out click-through instead of blocking the page once it's visually gone.
    const release = (): void => {
        shell.setAttribute('inert', '');
        document.documentElement.classList.remove('swl-player-locked');
        inertSiblings(false);
        trigger?.focus({ preventScroll: true });
    };

    const teardown = (): void => {
        shell.classList.remove('is-open', 'is-revealed');
        shell.removeAttribute('inert');
        shell.hidden = true;
        embed.replaceChildren();

        document.documentElement.classList.remove('swl-player-locked');
        inertSiblings(false);

        closing = false;
        trigger = null;
    };

    const open = (source: HTMLElement, id: string, type: MediaType): void => {
        const src = embedSrc(id, type);

        if (src === null) {
            return;
        }

        window.clearTimeout(closeTimer);
        closing = false;
        shell.removeAttribute('inert');
        trigger = source;

        const title = source.querySelector('.swl-media-title')?.textContent?.trim();
        mountEmbed(src, title !== undefined && title !== '' ? title : 'Twitch-speler');

        shell.hidden = false;
        document.documentElement.classList.add('swl-player-locked');
        inertSiblings(true);
        closeButton?.focus({ preventScroll: true });

        // Let the hidden→shown state commit before flipping the open/revealed classes,
        // so the dim fade and the mask-grow actually run as transitions.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            shell.classList.add('is-open', 'is-revealed');
        }));
    };

    const close = (): void => {
        if (shell.hidden || closing) {
            return;
        }

        closing = true;
        window.clearTimeout(closeTimer);

        if (reduced) {
            release();
            teardown();

            return;
        }

        // Phase one: shrink the scene back into the play button (the video fades out with it).
        shell.classList.remove('is-revealed');

        closeTimer = window.setTimeout(() => {
            // Shape is gone, so hand the page back now; the dim then fades out
            // click-through before the final teardown.
            release();
            shell.classList.remove('is-open');
            closeTimer = window.setTimeout(teardown, fadeMs);
        }, revealMs);
    };

    document.addEventListener('click', event => {
        // Let modified clicks (new tab/window) fall through to the Twitch href.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        const target = event.target;

        if (!(target instanceof Element)) {
            return;
        }

        const link = target.closest<HTMLElement>('[data-swl-player-trigger]');

        if (link === null) {
            return;
        }

        const id = link.dataset.mediaId;
        const type = link.dataset.mediaType;

        // Without the identifiers there is nothing to embed; let the link do its job.
        if (id === undefined || id === '' || (type !== 'clip' && type !== 'video')) {
            return;
        }

        event.preventDefault();
        open(link, id, type);
    });

    // A click anywhere on the scene that isn't the video itself dismisses the overlay.
    scene.addEventListener('click', event => {
        if (!(event.target instanceof Node) || !stage.contains(event.target)) {
            close();
        }
    });

    closeButton?.addEventListener('click', close);

    document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !shell.hidden) {
            close();
        }
    });
}
